import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject } from "rxjs";
import { AuthenticationService } from "src/app/services/authentication.service";
import { ConfigService } from "src/app/services/config.service";
import { ACTIVE_COMPANY_KEY } from "src/app/helpers/storage-keys";

export interface CompanyInfo {
    id: number;
    name: string;
}

// Client-side session service. Loads the signed-in user's Companies once at session start,
// exposes their role and an Active Company (persisted in localStorage with fallback) that
// client pages and the dashboard read instead of the old JWT single-company claim.
@Injectable()
export class CompanyService {
    allCompanies = false;
    role: string | null = null;
    companies: CompanyInfo[] = [];

    private activeCompanyIdSubject = new BehaviorSubject<number | null>(null);
    activeCompanyId$ = this.activeCompanyIdSubject.asObservable();

    private loaded = false;

    constructor(
        private httpClient: HttpClient,
        private configService: ConfigService,
        private authService: AuthenticationService,
    ) {
        this.authService.userProfileLoaded().subscribe(() => this.load());
    }

    load() {
        if (this.loaded) {
            return;
        }
        this.httpClient
            .get<any>(
                this.configService.apiBaseUrl.concat("User/MySenriClients"),
            )
            .subscribe(
                (data) => {
                    this.allCompanies = data.allCompanies;
                    this.role = data.role;
                    this.companies = data.companies || [];
                    this.resolveActiveCompany();
                    this.loaded = true;
                },
                () => {
                    // Allow a later attempt (e.g. next token event / app init) to retry.
                    this.loaded = false;
                },
            );
    }

    getActiveCompanyId(): number | null {
        return this.activeCompanyIdSubject.getValue();
    }

    setActiveCompany(id: number | null) {
        if (id !== null && !this.companies.some((c) => c.id === id)) {
            return;
        }
        this.persist(id);
        this.activeCompanyIdSubject.next(id);
    }

    private resolveActiveCompany() {
        if (this.allCompanies) {
            this.activeCompanyIdSubject.next(null);
            return;
        }
        let active =
            this.companies.length === 1
                ? this.companies[0].id
                : this.readStoredActiveCompany();
        if (active === null || !this.companies.some((c) => c.id === active)) {
            active = this.companies.length > 0 ? this.companies[0].id : null;
        }
        this.persist(active);
        this.activeCompanyIdSubject.next(active);
    }

    private readStoredActiveCompany(): number | null {
        const raw = localStorage.getItem(ACTIVE_COMPANY_KEY);
        if (raw === null) {
            return null;
        }
        const parsed = parseInt(raw, 10);
        return isNaN(parsed) ? null : parsed;
    }

    private persist(id: number | null) {
        if (id === null) {
            localStorage.removeItem(ACTIVE_COMPANY_KEY);
        } else {
            localStorage.setItem(ACTIVE_COMPANY_KEY, id.toString());
        }
    }
}
